import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { removeKeywordsFromStartPhrases } from '../../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { VirtualTreeHelpers } from '../../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import {
    PhrasesSelectorGroupKeywords,
    PhrasesSelectorPhrase,
    PhrasesSelectorStartPhraseNode
} from '../../../typings/phrases';
import { CheckboxDecorator } from '../../CheckboxDecorator/CheckboxDecorator';
import { GridHeaderCell } from '../../GridHeaderCell/GridHeaderCell';
import { PhrasesSelectorPhrasesTree } from '../PhrasesSelectorPhrasesTree/PhrasesSelectorPhrasesTree';

import './PhrasesSelectorStartPhrases.css';
import * as keyset from './PhrasesSelectorStartPhrases.i18n';

const i18n = i18nFactory(keyset);

export interface PhrasesSelectorStartPhrasesProps {
    areMinusPhrasesEnabled: boolean;
    startPhrases: Array<PhrasesSelectorStartPhraseNode>;
    errorMessage: string;
    keywords: Array<PhrasesSelectorPhrase>;
    groupKeywords: PhrasesSelectorGroupKeywords;
    onStartPhrasesChange: (nodes: Array<PhrasesSelectorStartPhraseNode>) => void;
    onKeywordAdd: (phrase: PhrasesSelectorPhrase) => void;
    onMinusPhraseAdd: (phrase: string) => void;
    onPhraseStatsFetch: (phrase: PhrasesSelectorStartPhraseNode) => void;
    onMinusWordAdd: (word: string) => void;
}

const BLOCK = 'phrases-selector-start-phrases';

export class PhrasesSelectorStartPhrases extends React.PureComponent<PhrasesSelectorStartPhrasesProps> {
    render() {
        const {
            areMinusPhrasesEnabled,
            startPhrases,
            keywords,
            groupKeywords,
            errorMessage,
            onStartPhrasesChange,
            onKeywordAdd,
            onMinusPhraseAdd,
            onPhraseStatsFetch,
            onMinusWordAdd
        } = this.props;

        const filteredPhrases = removeKeywordsFromStartPhrases(startPhrases, keywords);
        const allSelected = this.areAllPhrasesSelected(filteredPhrases);

        return (
            <div className={Bem.cls(BLOCK)}>
                <header className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'select-all-cell')}>
                        <CheckboxDecorator
                            size="s"
                            theme="normal"
                            tone="default"
                            view="default"
                            checked={allSelected}
                            onChecked={this.onSelectAll(allSelected, filteredPhrases)}
                        />
                    </div>
                    <GridHeaderCell
                        isSortable={true}
                        isSortKey={true}
                        title={i18n('phrases-cell-title')}
                        isTitleVisible={true}
                    />
                    <GridHeaderCell
                        isSortable={false}
                        isSortKey={false}
                        isTitleVisible={true}
                        title={i18n('shows-cell-title')}
                        className={Bem.cls(BLOCK, 'shows-cell')}
                    />
                </header>
                <PhrasesSelectorPhrasesTree
                    areMinusPhrasesEnabled={areMinusPhrasesEnabled}
                    errorMessage={errorMessage}
                    allPhrases={startPhrases}
                    phrases={filteredPhrases}
                    groupKeywords={groupKeywords}
                    onChange={onStartPhrasesChange}
                    onKeywordAdd={onKeywordAdd}
                    onMinusPhraseAdd={onMinusPhraseAdd}
                    onChildrenFetch={onPhraseStatsFetch}
                    onMinusWordAdd={onMinusWordAdd}
                />
            </div>
        );
    }

    private onSelectAll = (allSelected: boolean, nodes: Array<PhrasesSelectorStartPhraseNode>) => () => {
        const { startPhrases, onStartPhrasesChange } = this.props;

        const result = VirtualTreeHelpers.selectNodes(
            startPhrases,
            nodes.map(node => ({
                ...node,
                state: {
                    ...node.state,
                    selected: !allSelected
                }
            }))
        );

        onStartPhrasesChange(result);
    };

    private areAllPhrasesSelected = (phrases: Array<PhrasesSelectorStartPhraseNode>): boolean => {
        return phrases.length !== 0 ? phrases.map(VirtualTreeHelpers.isEveryChildSelected).every(Boolean) : false;
    };
}
